import React from 'react';
import { cn } from '../../utils/cn';

export interface BottomNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  action: () => void;
  badge?: number | string; // Optional badge count
  disabled?: boolean;
}

export interface BottomNavProps {
  items: BottomNavItem[];
  className?: string;
  lightTheme?: boolean;
}

/**
 * Bottom Navigation component for mobile interfaces
 * Features:
 * - Fixed bottom positioning with safe area support (iOS notch)
 * - Active state indicators
 * - Touch-friendly 56px height (WCAG AA compliant)
 * - Badge support for notifications
 * - Smooth transitions
 * - Keyboard accessible
 */
export const BottomNav: React.FC<BottomNavProps> = ({ items, className, lightTheme = false }) => {
  return (
    <nav
      role="navigation"
      aria-label="Navegação principal"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'h-14 border-t flex items-stretch',
        lightTheme ? 'bg-white border-gray-200' : 'bg-black border-gray-800',
        // Safe area padding for iOS devices with notch/home indicator
        'pb-[env(safe-area-inset-bottom)]',
        className
      )}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={item.action}
          disabled={item.disabled}
          aria-current={item.active ? 'page' : undefined}
          aria-label={item.label}
          className={cn(
            'flex-1 flex flex-col items-center justify-center gap-0.5',
            'relative transition-all duration-200',
            'focus:outline-none',
            // Active state
            item.active
              ? lightTheme ? 'bg-blue-50 text-blue-600' : 'bg-blue-600/10 text-blue-400'
              : lightTheme
                ? 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 active:bg-gray-200 focus:bg-gray-100'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/50 active:bg-gray-800 focus:bg-gray-900',
            // Disabled state
            item.disabled && 'opacity-50 cursor-not-allowed',
            // Touch feedback
            !item.disabled && 'active:scale-95'
          )}
        >
          {/* Active indicator bar */}
          {item.active && (
            <div className={cn('absolute top-0 left-0 right-0 h-0.5', lightTheme ? 'bg-blue-500' : 'bg-blue-500')} />
          )}

          {/* Icon */}
          <div className="relative flex-shrink-0">
            {item.icon}

            {/* Badge */}
            {item.badge !== undefined && (
              <span
                className={cn(
                  'absolute -top-1 -right-1',
                  'min-w-[16px] h-4 px-1',
                  'flex items-center justify-center',
                  'bg-red-500 text-white',
                  'text-[9px] font-bold leading-none',
                  lightTheme ? 'rounded-full border border-white' : 'rounded-full border border-black'
                )}
              >
                {typeof item.badge === 'number' && item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </div>

          {/* Label */}
          <span
            className={cn(
              'text-[10px] font-medium leading-tight truncate max-w-full px-1',
              item.active
                ? lightTheme ? 'text-blue-600' : 'text-blue-400'
                : lightTheme ? 'text-gray-500' : 'text-gray-400'
            )}
          >
            {item.label}
          </span>
        </button>
      ))}
    </nav>
  );
};
